/*!
 * Copyright (c) 2026 Elevation Technologies Pte Ltd.
 * Developer: filimoni@elevationtech.com.fj
 */
import React, { useState } from 'react';
import { FSF_BRAND } from '../fsfPages.generated';
import ContactIcon from '../components/ContactIcon';
import PhotoLightbox from '../components/PhotoLightbox';
import TabsBlock from '../components/TabsBlock';
import {
  LOGISTICS_FALLBACK_TABS,
  LOGISTICS_IMAGES,
  LOGISTICS_PHOTOS,
  LOGISTICS_SERVICE_META
} from '../data/siteData';
import { getPlainHtmlText } from '../utils/content';

export function LogisticsCopy({ html, className = '' }) {
  if (!html) {
    return null;
  }

  return <div className={`fsf-logistics-copy ${className}`.trim()} dangerouslySetInnerHTML={{ __html: html }} />;
}

export function LogisticsMedia({ photo, index, onOpen, className = '', loading = 'lazy' }) {
  if (!photo?.src) {
    return null;
  }

  return (
    <figure className={`fsf-logistics-media ${className}`.trim()}>
      <button
        className="fsf-logistics-media__button"
        type="button"
        onClick={() => onOpen(index)}
        aria-label={`Open ${photo.alt}`}
      >
        <img src={photo.src} alt={photo.alt} loading={loading} />
      </button>
    </figure>
  );
}

export function LogisticsServiceCard({ item, index }) {
  const meta = LOGISTICS_SERVICE_META.find((service) => service.title === item.title) || LOGISTICS_SERVICE_META[index];

  return (
    <article className="fsf-logistics-service-card">
      <span className="fsf-logistics-service-card__icon" aria-hidden="true">
        <ContactIcon name={meta?.icon || 'package'} />
      </span>
      <div className="fsf-logistics-service-card__body">
        <p>{String(index + 1).padStart(2, '0')}</p>
        <h3>{item.title}</h3>
        <span>{meta?.summary || 'End-to-end logistics support for commercial freight and supply chain movements.'}</span>
      </div>
    </article>
  );
}

export function getLogisticsTabs(page) {
  const tabsBlock = (page.blocks || []).find((block) => block?.type === 'tabs');

  return (tabsBlock?.items || []).map((item) => {
    const isPlaceholder = getPlainHtmlText(item.html) === 'tab content';

    return {
      ...item,
      html: isPlaceholder ? LOGISTICS_FALLBACK_TABS[item.title] || item.html : item.html,
    };
  });
}

export default function LogisticsPageSections({ page }) {
  const [activePhotoIndex, setActivePhotoIndex] = useState(null);
  const introBlock = (page.blocks || []).find((block) => block?.type === 'html');
  const tabs = getLogisticsTabs(page);

  return (
    <>
      <div className="fsf-logistics-story">
        <section className="fsf-logistics-section fsf-logistics-section--intro">
          <div className="fsf-logistics-section__inner fsf-logistics-intro">
            <div className="fsf-logistics-intro__copy">
              <p className="fsf-section-kicker">Logistics Division</p>
              <LogisticsCopy html={introBlock?.html} className="fsf-logistics-copy--intro" />
              <div className="fsf-logistics-highlights" aria-label="Logistics capabilities">
                <span>Air + Sea</span>
                <span>Customs</span>
                <span>Warehousing</span>
                <span>Transport</span>
                <span>Tracking</span>
              </div>
            </div>
            <LogisticsMedia
              photo={LOGISTICS_IMAGES.containerYard}
              index={0}
              onOpen={setActivePhotoIndex}
              className="fsf-logistics-media--hero"
              loading="eager"
            />
          </div>
        </section>

        <section className="fsf-logistics-section fsf-logistics-section--services">
          <div className="fsf-logistics-section__inner">
            <div className="fsf-logistics-section__header">
              <p className="fsf-section-kicker">What We Move</p>
              <h2>End-to-end freight and supply chain support</h2>
              <p>
                From origin coordination through final delivery, FSF keeps commercial freight moving with practical,
                connected services for Fiji and the wider Pacific.
              </p>
            </div>
            <div className="fsf-logistics-service-grid">
              {tabs.map((item, index) => (
                <LogisticsServiceCard key={`${item.title}-${index}`} item={item} index={index} />
              ))}
            </div>
          </div>
        </section>

        <section className="fsf-logistics-section fsf-logistics-section--details">
          <div className="fsf-logistics-section__inner">
            <div className="fsf-logistics-section__header">
              <p className="fsf-section-kicker">Service Details</p>
              <h2>Choose a logistics service to explore</h2>
            </div>
            <TabsBlock items={tabs} />
          </div>
        </section>

        <section className="fsf-logistics-section fsf-logistics-section--operations">
          <div className="fsf-logistics-section__inner fsf-logistics-operations">
            <div className="fsf-logistics-section__header">
              <p className="fsf-section-kicker">Operations On The Ground</p>
              <h2>Visibility from the yard to the warehouse</h2>
              <p>
                The logistics team brings together equipment, trained staff, approved facilities, and customer-facing
                communication so freight can keep moving with fewer surprises.
              </p>
            </div>
            <div className="fsf-logistics-photo-grid">
              <LogisticsMedia photo={LOGISTICS_IMAGES.containerStack} index={1} onOpen={setActivePhotoIndex} className="fsf-logistics-media--wide" />
              <LogisticsMedia photo={LOGISTICS_IMAGES.warehouse} index={2} onOpen={setActivePhotoIndex} className="fsf-logistics-media--tall" />
              <LogisticsMedia photo={LOGISTICS_IMAGES.customs} index={3} onOpen={setActivePhotoIndex} />
              <LogisticsMedia photo={LOGISTICS_IMAGES.coordination} index={4} onOpen={setActivePhotoIndex} />
            </div>
          </div>
        </section>

        <section className="fsf-logistics-section fsf-logistics-section--cta">
          <div className="fsf-logistics-section__inner fsf-logistics-cta">
            <div>
              <p className="fsf-section-kicker">Ready To Move Cargo?</p>
              <h2>Get the right logistics team around the shipment.</h2>
            </div>
            <div className="fsf-logistics-cta__actions">
              <a className="fsf-button" href={FSF_BRAND.quoteUrl} target="_blank" rel="noopener noreferrer">Get a Quotation</a>
              <a className="fsf-button fsf-button--secondary" href="/contactus/">Contact FSF</a>
            </div>
          </div>
        </section>
      </div>

      <PhotoLightbox
        images={LOGISTICS_PHOTOS}
        activeIndex={activePhotoIndex}
        setActiveIndex={setActivePhotoIndex}
        label="Freight Services Fiji logistics photos"
      />
    </>
  );
}
